import { Injectable } from '@angular/core';
import { BehaviorSubject, from, switchMap } from 'rxjs';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { environment } from 'src/environments/environment';
import { DataService } from './data.service';
import { ApiEndPointUrl } from 'src/app/utils/systemEnums';
import { AuthServiceLocal } from './auth.service';

export interface PushBannerData {
  title: string;
  body: string;
  actionUrl?: string;
  autoCloseAfter?: string | number;
}

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private pushBannerState = new BehaviorSubject<PushBannerData | null>(null);
  public pushBanner$ = this.pushBannerState.asObservable();
  private recentMessageKeys = new Map<string, number>();
  private readonly DEDUPE_WINDOW_MS = 5000;

  constructor(
    private dataService: DataService,
    private authService: AuthServiceLocal,
  ) { }

  public initializeWebPush(): void {
    if (typeof window === 'undefined' || typeof Notification === 'undefined') {
      return;
    }

    isSupported()
      .then((supported) => {
        if (!supported) {
          return;
        }

        this.listenForegroundMessages();
        this.listenServiceWorkerMessages();

        if (Notification.permission === 'granted') {
          this.resolveToken();
        } else if (Notification.permission === 'default') {
          this.requestPermissionAndRegister();
        }
      })
      .catch(() => {
        // Ignore unsupported browser errors silently.
      });
  }

  public requestPermissionAndRegister(): void {
    if (typeof Notification === 'undefined') {
      return;
    }

    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        this.resolveToken();
      }
    });
  }

  public dismissBanner(): void {
    this.pushBannerState.next(null);
  }

  private listenForegroundMessages(): void {
    const messaging = getMessaging();

    onMessage(messaging, (payload) => {
      console.log('FCM foreground payload:', payload);
      const notification = payload.notification || {};
      const data = payload.data || {};
      const messageId = (payload as any)?.messageId || (payload as any)?.fcmMessageId;
      if (this.isDuplicateMessage(messageId, payload)) {
        return;
      }
      const clickAction =
        data['click_action'] ||
        (notification as any)['click_action'] ||
        (payload as any)?.fcmOptions?.link ||
        '/';
      const bannerData: PushBannerData = {
        title: notification.title || data['title'] || 'New notification',
        body: notification.body || data['body'] || '',
        actionUrl: this.normalizeActionUrl(clickAction),
        autoCloseAfter: data['autoCloseAfter']
      };

      if (this.isTabActive()) {
        this.showBanner(bannerData);
        return;
      }

      this.showBrowserNotification(bannerData);
    });
  }

  private isDuplicateMessage(messageId: string | undefined, payload: any): boolean {
    const now = Date.now();
    for (const [key, ts] of this.recentMessageKeys) {
      if (now - ts > this.DEDUPE_WINDOW_MS) {
        this.recentMessageKeys.delete(key);
      }
    }
    const key = messageId
      || [
        payload?.notification?.title || payload?.payload?.title || payload?.data?.title || '',
        payload?.notification?.body || payload?.payload?.body || payload?.data?.body || '',
      ].join('|');
    if (!key) {
      return false;
    }
    if (this.recentMessageKeys.has(key)) {
      return true;
    }
    this.recentMessageKeys.set(key, now);
    return false;
  }

  private listenServiceWorkerMessages(): void {
    if (!navigator.serviceWorker) {
      return;
    }

    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
      const payload = event?.data;
      console.log('FCM service-worker message payload:', payload);
      if (payload?.type === 'FCM_NOTIFICATION_CLICK') {
        const target = payload?.payload?.clickAction || '/';
        window.location.href = this.normalizeActionUrl(target);
        return;
      }
      if (payload?.type !== 'FCM_BACKGROUND_MESSAGE' && payload?.messageType !== 'push-received') {
        return;
      }

      const messageId = payload?.fcmMessageId || payload?.payload?.fcmMessageId;
      if (this.isDuplicateMessage(messageId, payload)) {
        return;
      }

      const bannerData: PushBannerData = {
        title: payload?.notification?.title || payload?.payload?.title || payload?.data?.title || 'New notification',
        body: payload?.notification?.body || payload?.payload?.body || payload?.data?.body || '',
        actionUrl: this.normalizeActionUrl(payload?.notification?.click_action || payload?.payload?.clickAction || payload?.data?.click_action || '/'),
        autoCloseAfter: payload?.data?.autoCloseAfter || payload?.payload?.data?.autoCloseAfter || payload?.payload?.autoCloseAfter
      };

      if (this.isTabActive()) {
        this.showBanner(bannerData);
      }
    });
  }

  private resolveToken(): void {
    const vapidKey = environment?.webPush?.vapidKey;
    if (!vapidKey) {
      return;
    }

    const messaging = getMessaging();

    getToken(messaging, { vapidKey })
      .then((token) => {
        if (!token) {
          return;
        }
        console.log('FCM device token:', token);
        this.saveUserFcmToken(token);
      })
      .catch(() => {
        // Token generation can fail for unsupported/browser policy cases.
      });
  }

  private saveUserFcmToken(token: string): void {
    const payload = {
      deviceToken: token,
    };
    console.log('FCM save payload:', payload);

    from(this.resolveApiVersion())
      .pipe(
        switchMap((apiVersion) => 
          this.dataService.postDataWithHeader(
            `${ApiEndPointUrl}v${apiVersion}/profile/deviceinfo/save`,
            payload,
          ),
        ),
      )
      .subscribe({
        next: () => { },
        error: () => { },
      });
  }

  private async resolveApiVersion(): Promise<string> {
    if (this.authService.api_version) {
      return this.authService.api_version;
    }

    await this.authService.getApiVersion();
    return this.authService.api_version || '13';
  }

  private showBanner(banner: PushBannerData): void {
    this.pushBannerState.next(banner);
  }

  private isTabActive(): boolean {
    return !document.hidden && document.visibilityState === 'visible' && document.hasFocus();
  }

  private showBrowserNotification(notification: PushBannerData): void {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      return;
    }

    if (navigator.serviceWorker?.ready) {
      navigator.serviceWorker.ready
        .then((registration) => {
          registration.showNotification(notification.title, {
            body: notification.body,
            icon: '/assets/icons/icon-192x192.png',
            tag: notification.title,
            data: { click_action: this.normalizeActionUrl(notification.actionUrl || '/') },
          });

          const timeoutMs = this.resolveAutoCloseMs(notification.autoCloseAfter);
          if (timeoutMs > 0) {
            setTimeout(() => {
              registration.getNotifications({ tag: notification.title }).then((notifications) => {
                notifications.forEach(n => n.close());
              });
            }, timeoutMs);
          }
        })
        .catch(() => { });
      return;
    }

    const browserNotification = new Notification(notification.title, {
      body: notification.body,
      icon: '/assets/icons/icon-192x192.png',
      data: { click_action: this.normalizeActionUrl(notification.actionUrl || '/') },
    });
    browserNotification.onclick = () => {
      window.focus();
      const target = this.normalizeActionUrl(notification.actionUrl || '/');
      window.location.href = target;
    };

    const fallbackTimeoutMs = this.resolveAutoCloseMs(notification.autoCloseAfter);
    if (fallbackTimeoutMs > 0) {
      setTimeout(() => {
        browserNotification.close();
      }, fallbackTimeoutMs);
    }
  }

  private resolveAutoCloseMs(autoCloseAfter?: string | number): number {
    if (autoCloseAfter === undefined || autoCloseAfter === null || autoCloseAfter === '') {
      return 0;
    }
    const parsed = Number(autoCloseAfter);
    if (isNaN(parsed) || parsed <= 0) {
      return 0;
    }
    return parsed;
  }

  private normalizeActionUrl(actionUrl: string): string {
    try {
      return new URL(actionUrl || '/', window.location.origin).href;
    } catch (_) {
      return window.location.origin + '/';
    }
  }

}
